import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import isEqual from 'lodash-es/isEqual';

import { SimpleChanges } from '@core-models/utilities/generic-simple-changes';
import { EmailAlertTimeOption } from '@settings/enums/email-alert-time-option.enum';
import { CustomerSettings } from '@settings/models/settings/customer-settings';
import { NotificationAlertsSettings } from '@settings/models/settings/notification-alerts-settings';
import { RpcSelectControltOptions } from '@core-controls/components/rpc-select/models/rpc-select-controlt-options';
import { RpcSelectOption } from '@core-controls/components/rpc-select/models/rpc-select-option';
import { ListingActivityConstants } from '@listings/constants/listing-activity-constants';
import { FieldGroup, Segment } from './models/field-group';
import { NotificationType } from 'app/modules/core-modules/enums/notification-type';
import { ToastService } from '@core-services/toast.service';
import { RpcIconComponent } from '../../../../../core-modules/utils/rpc-icon/rpc-icon.component';
import { RpcSlideToggleComponent } from '../../../../../core-modules/controls/components/rpc-slide-toggle/rpc-slide-toggle.component';
import { RpcSelectComponent } from '../../../../../core-modules/controls/components/rpc-select/rpc-select.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'email-alert-settings',
    templateUrl: './email-alert-settings.component.html',
    styleUrls: ['./email-alert-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RpcIconComponent, RpcSlideToggleComponent, RpcSelectComponent, TranslateModule]
})
export class EmailAlertSettingsComponent implements OnChanges {

    @Input() settings: CustomerSettings;
    @Input() settingsFor: 'appointment' | 'listing';
    @Input() isSmsAvailable = true;
    @Input() hasSmsAlertsNumber = false;
    @Input() isSelfProfile = true;
    @Output() settingsChanged: EventEmitter<CustomerSettings> = new EventEmitter<CustomerSettings>();

    public emailForm: UntypedFormGroup;
    public smsForm: UntypedFormGroup;
    public fieldGroup: FieldGroup;

    public readonly controlIconMap = new Map<keyof NotificationAlertsSettings, { title: string, icon?: string, iconClass?: string }>([
        ['newListing', { title: 'EMAIL_ALERT_SETTINGS.LISTINGS.ADDED', icon: ListingActivityConstants.PickListed.icon, iconClass: 'rpc-color-picked' }],
        ['like', { title: 'EMAIL_ALERT_SETTINGS.LISTINGS.LIKED', icon: ListingActivityConstants.Liked.icon, iconClass: 'rpc-color-liked' }],
        ['dislike', { title: 'EMAIL_ALERT_SETTINGS.LISTINGS.DISLIKED', icon: ListingActivityConstants.Disliked.icon, iconClass: 'rpc-color-disliked' }],
        ['remove', { title: 'EMAIL_ALERT_SETTINGS.LISTINGS.REMOVED', icon: 'delete-bin', iconClass: 'rpc-color-removed' }],
        ['comment', { title: 'EMAIL_ALERT_SETTINGS.LISTINGS.MESSAGES', icon: 'comments' }],
        ['appointmentConfirm', { title: 'EMAIL_ALERT_SETTINGS.APPOINTMENTS.CONFIRMATIONS' }],
        ['appointmentRequest', { title: 'EMAIL_ALERT_SETTINGS.APPOINTMENTS.REQUESTS' }],
        ['appointmentDecline', { title: 'EMAIL_ALERT_SETTINGS.APPOINTMENTS.DECLINES' }]
    ]);

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly toastService: ToastService
    ) { }

    public ngOnChanges(changes: SimpleChanges<EmailAlertSettingsComponent>): void {
        if (changes.settings?.currentValue != null &&
            (changes.settings.previousValue == null || !isEqual(changes.settings.currentValue, changes.settings.previousValue))
        ) {
            if (this.emailForm == null || this.smsForm == null) {
                this.emailForm = this.formBuilder.group({});
                this.smsForm = this.formBuilder.group({});

                this.createFormControls(this.settingsFor);
            }

            this.emailForm.patchValue(changes.settings.currentValue.emailAlerts);
            this.smsForm.patchValue(changes.settings.currentValue.smsAlerts);
        }
    }

    public onSettingChanged(key: keyof NotificationAlertsSettings, value: any, notificationType: NotificationType) {
        const email = notificationType === NotificationType.Email
            ? { ...this.settings.emailAlerts, [key]: value }
            : this.settings.emailAlerts;

        const sms = notificationType === NotificationType.Email
            ? this.settings.smsAlerts
            : { ...this.settings.smsAlerts, [key]: value };

        this.settingsChanged.next(new CustomerSettings(email, sms, this.settings.layoutSettings, this.settings.permissionSettings, this.hasSmsAlertsNumber));
    }

    public showSmsError(): void {
        const errorKey = this.isSelfProfile
            ? 'EMAIL_ALERT_SETTINGS.ERRORS.INVALID_MY_PHONE'
            : 'EMAIL_ALERT_SETTINGS.ERRORS.INVALID_PHONE';

        this.toastService.showClientError(errorKey);
    }

    private createFormControls(settingsFor: 'appointment' | 'listing'): void {
        this.fieldGroup = settingsFor === 'appointment'
            ? this.getGroup('EMAIL_ALERT_SETTINGS.APPOINTMENTS.TITLE', 'enableAppointmentAlert', this.getAppointmentFields)
            : this.getGroup('EMAIL_ALERT_SETTINGS.LISTINGS.TITLE', 'enableListingAlert', this.getListingFields);
    }

    private getGroup(
        title: string,
        toggleKey: keyof NotificationAlertsSettings,
        getter: (alertSettings: NotificationAlertsSettings) => Partial<NotificationAlertsSettings>): FieldGroup {

        const smsSegment = this.getSegment(NotificationType.Sms, toggleKey, getter);
        const emailSegment = this.getSegment(NotificationType.Email, toggleKey, getter);

        return {
            title,
            segments: [smsSegment, emailSegment],
            fieldTitles: smsSegment.fields.map(({ title, icon, iconClass }) => ({ title, icon, iconClass }))
        };
    }

    private getSegment(
        notificationType: NotificationType,
        toggleKey: keyof NotificationAlertsSettings,
        getter: (alertSettings: NotificationAlertsSettings) => Partial<NotificationAlertsSettings>): Segment {

        const alertSettings = notificationType === NotificationType.Email
            ? this.settings.emailAlerts
            : this.settings.smsAlerts;

        const formGroup = notificationType === NotificationType.Email
            ? this.emailForm
            : this.smsForm;

        const alerts = getter(alertSettings);
        const fields = this.getFields(alerts, formGroup);

        return {
            fields,
            notificationType,
            toggleField: {
                formGroup,
                key: toggleKey,
                controlName: toggleKey,
                value: !!alertSettings[toggleKey],
                isAvailable: notificationType === NotificationType.Email || this.isSmsAvailable,
                title: notificationType === NotificationType.Email
                    ? 'EMAIL_ALERT_SETTINGS.NOTIFICATION_TYPE.EMAIL'
                    : 'EMAIL_ALERT_SETTINGS.NOTIFICATION_TYPE.SMS'
            }
        };
    }

    private readonly getFields = (alerts: Partial<NotificationAlertsSettings>, form: UntypedFormGroup) => {
        return Object.entries(alerts)
            .map(([key, value]: [keyof NotificationAlertsSettings, number]) => ({
                key,
                controlOptions: new RpcSelectControltOptions(form, key, this.getSelectOptions(), value),
                ...this.controlIconMap.get(key)
            }));
    };

    private getSelectOptions(): RpcSelectOption[] {
        return [
            {
                text: 'EMAIL_ALERT_SETTINGS.OPTIONS.DAILY',
                value: EmailAlertTimeOption.Daily
            },
            {
                text: 'EMAIL_ALERT_SETTINGS.OPTIONS.REAL_TIME',
                value: EmailAlertTimeOption.RealTime
            },
            {
                text: 'EMAIL_ALERT_SETTINGS.OPTIONS.DISABLED',
                value: EmailAlertTimeOption.Disabled
            },
        ];
    }

    private getListingFields(alertSettings: NotificationAlertsSettings): Partial<NotificationAlertsSettings> {
        const { newListing, like, dislike, remove, comment } = alertSettings;

        return {
            newListing,
            like,
            dislike,
            remove,
            comment
        };
    }

    private getAppointmentFields(alertSettings: NotificationAlertsSettings): Partial<NotificationAlertsSettings> {
        const { appointmentConfirm, appointmentDecline, appointmentRequest } = alertSettings;

        return {
            appointmentConfirm,
            appointmentDecline,
            appointmentRequest
        };
    }
}